import {
    TrendingHazardDTO,
    colors,
    Organization,
} from '@hazadapt-git/public-core-base'
import React, { FC } from 'react'
import { makeStyles } from 'tss-react/mui'
import {
    InsightGrid,
    InsightsLoadingOverlay,
    QueryBuilder,
    TrendingHazardList,
} from '../organisms'
import {
    InsightViewProps,
    LastUpdatedWithReload,
    LocalContentDataDisclaimer,
} from '../molecules'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { IoDownload, IoEllipsisVertical } from 'react-icons/io5'
import { primaryIconSize, theme } from '../../lib/styles/universal'
import { useWindowSizeUp } from '../../lib/utils'
import { CustomMenuItem } from '../../lib/entities/misc'
import { v4 } from 'uuid'
import { LoadingButton } from '../atoms'
import { QueryBuilderValues } from '../organisms/QueryBuilder'

interface HomePageTemplateProps {
    pageTitle: string
    selectedAreaName: string
    dateRange: string
    trendingHazardData: TrendingHazardDTO[]
    showLocalContentDisclaimer?: boolean
    onReloadPress(): void
    updatedDate?: Date
    onDownloadPress: () => Promise<void>
    insights?: InsightViewProps[]
    quickStatRowTitle?: string
    quickStatRowSubtitle?: string[]
    loading?: boolean
    retrievingInsights?: boolean
    onPressTrendingHazard(id: number): void
    queryId?: string
    organization: Organization
    selectedAreas: string[]
    startDate: Date | null
    endDate: Date | null
    onRunQueryPress(values: QueryBuilderValues): Promise<void> | void
    onResetQueryPress(): void
    onSaveQueryPress: React.MouseEventHandler
    onDownloadReportPress: React.MouseEventHandler
}

const pageId = v4()
export const HomePageTemplate: FC<HomePageTemplateProps> = (
    props: HomePageTemplateProps
) => {
    const { classes: localClasses } = useLocalStyles()
    const mdScreens = useWindowSizeUp('md')

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const pageIsLoading = props.loading || props.retrievingInsights

    const onOptionsClick = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget)
        },
        []
    )

    const onOptionsClose = () => {
        setAnchorEl(null)
    }

    React.useEffect(() => {
        if (pageIsLoading) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'auto'
        }

        return () => {
            document.body.style.overflow = 'auto'
        }
    }, [pageIsLoading])

    return (
        <div className={localClasses.container}>
            {pageIsLoading && <InsightsLoadingOverlay />}
            <TrendingHazardList
                data={props.trendingHazardData}
                onPress={props.onPressTrendingHazard}
                loading={props.loading}
            />
            <div className={localClasses.pageTitleContainer}>
                <div className={localClasses.pageTitle}>
                    <Typography
                        variant={mdScreens ? 'h2' : 'h3'}
                        component="h1"
                    >
                        {props.pageTitle}
                    </Typography>
                    {props.selectedAreaName || props.dateRange ? (
                        <Typography variant="h4" component="h2">
                            {props.selectedAreaName}
                            {props.dateRange ? ` | ${props.dateRange}` : null}
                        </Typography>
                    ) : pageIsLoading ? (
                        <Skeleton
                            width="min(80vw, 25rem)"
                            height={theme.typography.h2.lineHeight}
                        />
                    ) : null}
                </div>
                <div className={localClasses.pageTitleInfoContainer}>
                    <LastUpdatedWithReload
                        date={props.updatedDate}
                        onReload={props.onReloadPress}
                    />
                    {pageIsLoading ? (
                        <Skeleton
                            variant="rounded"
                            width={mdScreens ? '11rem' : '2.5rem'}
                            height={'2.5rem'}
                            sx={{ borderRadius: '3rem' }}
                        />
                    ) : (
                        <>
                            {mdScreens ? (
                                <LoadingButton
                                    onClickAsync={props.onDownloadPress}
                                >
                                    Download Report
                                </LoadingButton>
                            ) : (
                                <IconButton onClick={onOptionsClick}>
                                    <IoEllipsisVertical
                                        size={primaryIconSize}
                                        color={colors.primary.CERULEAN}
                                        aria-label="Open page options menu"
                                    />
                                </IconButton>
                            )}
                        </>
                    )}
                </div>
            </div>
            <div className={localClasses.queryBuilderWrapper}>
                <QueryBuilder
                    organization={props.organization}
                    pickerOneOptions={[]}
                    showPickerOne={false}
                    areaPickerLabel="Area"
                    areaPickerPlaceholder={
                        props.organization.jurisdiction_name ??
                        'Full Jurisdiction'
                    }
                    selectedAreas={props.selectedAreas}
                    startDate={props.startDate}
                    endDate={props.endDate}
                    onRunQueryPress={props.onRunQueryPress}
                    onResetQueryPress={props.onResetQueryPress}
                    onSaveQueryPress={props.onSaveQueryPress}
                    onDownloadPress={props.onDownloadReportPress}
                />
            </div>
            <div className={localClasses.insightGridContainer}>
                {!props.retrievingInsights &&
                    props.showLocalContentDisclaimer && (
                        <LocalContentDataDisclaimer />
                    )}
                <InsightGrid
                    data={props.insights}
                    quickStatRowTitle={props.quickStatRowTitle}
										quickStatRowSubtitle={props.quickStatRowSubtitle}
                    pageId={pageId}
                    selectedDatesString={props.dateRange}
                    selectedLocationString={props.selectedAreaName}
                />
            </div>
            <Menu
                id="query-actions"
                anchorEl={anchorEl}
                open={open}
                onClose={onOptionsClose}
                MenuListProps={{
                    'aria-labelledby': 'options-button',
                }}
            >
                <CustomMenuItem
                    icon={<IoDownload size={primaryIconSize} />}
                    label="Download Report"
                    onClick={() => {
                        props.onDownloadPress()
                        onOptionsClose()
                    }}
                />
            </Menu>
        </div>
    )
}

const useLocalStyles = makeStyles()({
    container: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        position: 'relative',
    },
    pageTitleContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        padding: '1.25rem 0',
        gap: '0.5rem',
        [theme.breakpoints.up('lg')]: {
            flexDirection: 'row',
            justifyContent: 'space-between',
        },
    },
    pageTitleInfoContainer: {
        display: 'flex',
        alignItems: 'center',
        padding: '0 1rem 0 0.5rem',
        gap: '1rem',
        justifyContent: 'space-between',
        [theme.breakpoints.down('lg')]: {
            width: 'calc(100% - 1.5rem)',
        },
        [theme.breakpoints.up('lg')]: {
            gap: '1.5rem',
        },
    },
    pageTitle: {
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: '1rem',
    },
    queryBuilderWrapper: {
        padding: '0 1rem 1rem 1rem',
    },
    insightGridContainer: {
        padding: '1rem 0',
        margin: '0 1rem',
        borderTop: `1px ${colors.grays.NOIR} solid`,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
    },
})
